import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Card,
    Col,
    Container,
    Form,
    ListGroup,
    OverlayTrigger,
    Row,
    Tooltip,
    InputGroup,
    Alert,
    Spinner,
} from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router';
import { Routes, Route, Navigate } from 'react-router-dom';
import Warning from 'react-ionicons/lib/Warning';
import MainLayout from '../../layouts/MainLayout';
import Overlay from '../../components/Overlay/Overlay';
import languageManager from '../../helpers/Language';
import AppTab from '../../components/AppTab/AppTab';
import { Admin, User } from '../../api';
import AnonymousAvatarURL from '../../assets/img/anonyme_avatar.png';
import AppPermission from '../../components/PortalAppPermission/PortalAppPermission';
import DocumentPermission from '../../components/DocumentPermission/DocumentPermission';
import {
    getCurrentVAccount,
    getCurrentSubVAccounts,
} from '../../helpers/Session';
import UserAnalytic from '../../helpers/UserAnalytic';
import { User as UserAPI } from '../../api';
import { connect } from 'react-redux';
import PermissionManager, {
    default as PM,
} from '../../helpers/PermissionManager';
import './AccessManagementUser.scss';
import { FFDAReportPermission } from '../../components/FFDAReportPermission/FFDAReportPermission';

const GET_PREFIX_ROUTE = (route) => {
    return `/settings/access-management/user/:userId${route}`;
};

const GET_ROUTE = (userId, route) => {
    return `/settings/access-management/user/${userId}${route}`;
};

const InformationView = ({ profile, user }) => {
    const [formData, setFormData] = useState({
        firstname: user.firstname,
        lastname: user.lastname,
    });
    const [initialized, setInitialized] = useState(false);
    const [updatedEmail, setUpdatedEmail] = useState(user.email);
    const [updateEmailToken, setUpdateEmailToken] = useState(null);
    const [updateEmailError, setUpdateEmailError] = useState(null);
    const [isUpdateEmailSubmitting, setIsUpdateEmailSubmitting] =
        useState(false);

    useEffect(() => {
        const init = async () => {
            try {
                const currentToken = await UserAPI.getUpdateEmailToken(
                    user.userId
                );
                setUpdateEmailToken(currentToken);
                setInitialized(true);
            } catch (err) {
                console.log(err);
            }
        };

        if (!initialized) {
            init();
        }
    }, [user, initialized, updateEmailToken]);

    const handleEmailUpdate = async (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsUpdateEmailSubmitting(true);

        try {
            await UserAPI.updateUserEmail(user.userId, updatedEmail);
            const message = isConsultingOwnProfile
                ? languageManager.T('SETTINGS_ACCESS_INFO_UPDATE_EMAIL_SENT')
                : languageManager.T(
                      'SETTINGS_ACCESS_INFO_UPDATE_EMAIL_UPDATED'
                  );
            setIsUpdateEmailSubmitting(false);
            setUpdateEmailError({ error: false, message: message });
        } catch (err) {
            const message =
                err?.response?.data?.information ??
                "Can't update user email, try again later.";
            setIsUpdateEmailSubmitting(false);
            setUpdateEmailError({ error: true, message: message });
        }
    };
    const handleOnSubmit = (e) => {
        if (e && e.preventDefault) {
            e.preventDefault();
            e.stopPropagation();
        }
    };
    const onAlertClose = () => {
        setUpdateEmailError(null);
    };
    const onEmailChange = (event) => {
        const { value } = event.target;
        setUpdatedEmail(value);
    };
    const setValue = (key, e) => {
        const v = { ...formData, [key]: e.target.value };
        setFormData(v);
    };

    const dataAreIdentical =
        user.firstname + user.lastname ===
        formData.firstname + formData.lastname;
    const emailIsIdentical = user.email === updatedEmail;
    const isConsultingOwnProfile = user.userId === profile.userId;

    return (
        <Card className="shadow-sm">
            <Card.Body>
                {updateEmailError !== null && (
                    <Alert
                        variant={updateEmailError.error ? 'danger' : 'success'}
                        className="text-center"
                        onClose={onAlertClose}
                        dismissible
                    >
                        {updateEmailError.message}
                    </Alert>
                )}
                {updateEmailToken !== null && isConsultingOwnProfile && (
                    <Alert variant={'info'} className="text-center">
                        {languageManager.T(
                            'SETTINGS_ACCESS_INFO_UPDATE_EMAIL_EXISTS'
                        )}
                        :<br />
                        <strong>{updateEmailToken?.updatedEmail ?? ''}</strong>
                    </Alert>
                )}
                <div className="h3">Information</div>
                <Form as={Row} onSubmit={handleEmailUpdate}>
                    <Form.Group
                        as={Row}
                        className="mb-3"
                        controlId="formPlaintextEmail"
                    >
                        {user.isBusinessOwner && !isConsultingOwnProfile && (
                            <>
                                <Form.Label column sm="2" className="fw-bold">
                                    Email address
                                </Form.Label>
                                <Form.Label
                                    column
                                    sm="10"
                                    className="fst-italic"
                                >
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip>
                                                You want to change the email
                                                address? Please call us.
                                            </Tooltip>
                                        }
                                    >
                                        <span>
                                            {user.email}
                                            <Warning
                                                width="24px"
                                                height="24px"
                                            />
                                        </span>
                                    </OverlayTrigger>
                                </Form.Label>
                            </>
                        )}
                        {((user.isBusinessOwner && isConsultingOwnProfile) ||
                            !user.isBusinessOwner) && (
                            <InputGroup>
                                <Form.Label column sm="2" className="fw-bold">
                                    Email address
                                </Form.Label>
                                <Form.Control
                                    type="email"
                                    value={updatedEmail}
                                    onChange={onEmailChange}
                                    disabled={!!updateEmailToken}
                                />
                                <Button
                                    disabled={
                                        emailIsIdentical || !!updateEmailToken
                                    }
                                    variant="primary"
                                    type="submit"
                                    onClick={handleEmailUpdate}
                                >
                                    {isUpdateEmailSubmitting ? (
                                        <>
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                                className="me-2"
                                            />
                                            Updating...
                                        </>
                                    ) : (
                                        <>Update</>
                                    )}
                                </Button>
                            </InputGroup>
                        )}
                    </Form.Group>
                </Form>
                <Form as={Row} onSubmit={handleOnSubmit}>
                    <Col>
                        <Form.Group
                            className="mb-3"
                            controlId="formdata.firstname"
                        >
                            <Form.Label className="fw-bold">
                                First name
                            </Form.Label>
                            <Form.Control
                                disabled
                                type="text"
                                value={formData.firstname}
                                onChange={(e) => setValue('firstname', e)}
                            />
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group
                            className="mb-3"
                            controlId="formdata.lastname"
                        >
                            <Form.Label className="fw-bold">
                                Last name
                            </Form.Label>
                            <Form.Control
                                disabled
                                type="text"
                                value={formData.lastname}
                                onChange={(e) => setValue('lastname', e)}
                            />
                        </Form.Group>
                    </Col>
                </Form>
                <Button variant="primary" disabled={dataAreIdentical}>
                    Save
                </Button>
            </Card.Body>
        </Card>
    );
};

const PermissionView = ({
    user,
    profile,
    session,
    maxPortalPermission,
    permissions,
    handleUpdateUserPortalPermission,
    drivePermission,
    handleUpdateUserDrivePermission,
    reportingPermission,
    handleUpdateUserReportingPermission,
    currentVAccount,
    subVAccounts,
    setCurrentVAccount,
}) => {
    if (!currentVAccount) return null;
    return (
        <>
            {subVAccounts.length > 0 ? (
                <Card className="shadow-sm mb-3">
                    <Card.Body>
                        <div
                            className="fw-bold text-uppercase text-muted mb-2"
                            style={{ fontSize: 14 }}
                        >
                            {subVAccounts.length <= 1
                                ? 'Business'
                                : 'Businesses'}
                        </div>
                        <select
                            className="form-control"
                            onChange={(e) => setCurrentVAccount(e.target.value)}
                            value={currentVAccount.entityId}
                        >
                            {subVAccounts.map((item, idx) => (
                                <option key={idx} value={item.entityId}>
                                    {item.name}
                                </option>
                            ))}
                        </select>
                    </Card.Body>
                </Card>
            ) : null}
            <Card className="shadow-sm">
                <Card.Body className="p-0">
                    {PM.isNotFederated(session, profile) && (
                        <AppTab>
                            <AppTab.Item label="General">
                                <div className="p-2">
                                    <AppPermission
                                        user={user}
                                        maxPermission={maxPortalPermission}
                                        permissions={permissions}
                                        handleUpdateUserPortalPermission={
                                            handleUpdateUserPortalPermission
                                        }
                                    />
                                </div>
                            </AppTab.Item>
                            <AppTab.Item label="Documents">
                                <div className="p-2">
                                    <DocumentPermission
                                        user={user}
                                        permissions={drivePermission}
                                        handleUpdateUserDrivePermission={
                                            handleUpdateUserDrivePermission
                                        }
                                    />
                                </div>
                            </AppTab.Item>
                        </AppTab>
                    )}
                    {PM.isFederated(session, profile) && (
                        <AppTab>
                            <AppTab.Item label="General">
                                <div className="p-2">
                                    <AppPermission
                                        user={user}
                                        maxPermission={maxPortalPermission}
                                        permissions={permissions}
                                        handleUpdateUserPortalPermission={
                                            handleUpdateUserPortalPermission
                                        }
                                        currentVAccount={currentVAccount}
                                    />
                                </div>
                            </AppTab.Item>
                            <AppTab.Item label="Documents">
                                <div className="p-2">
                                    <DocumentPermission
                                        user={user}
                                        permissions={drivePermission}
                                        handleUpdateUserDrivePermission={
                                            handleUpdateUserDrivePermission
                                        }
                                    />
                                </div>
                            </AppTab.Item>
                            {/* FFDA Reports */}
                            <AppTab.Item label="Reports">
                                <div className="p-2">
                                    <FFDAReportPermission
                                        user={user}
                                        permissions={reportingPermission}
                                        onPermissionChange={
                                            handleUpdateUserReportingPermission
                                        }
                                        currentVAccount={currentVAccount}
                                    />
                                </div>
                            </AppTab.Item>
                        </AppTab>
                    )}
                </Card.Body>
            </Card>
        </>
    );
};

const SettingsAccessManagementUser = ({ profile, session }) => {
    const navigateManager = useNavigate();
    const params = useParams();
    const [isLoading, setLoadingStatus] = useState(true);
    const [isInit, setInitStatus] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [userPermission, setUserPermission] = useState(null);
    const [maxPortalPermission, setMaxPortalPermission] = useState(null);
    const [uDrivePermission, setUserDrivePermission] = useState(null);
    const [uReportingPermission, setUserReportingPermission] = useState(null);
    const subVAccounts = useMemo(
        () => [
            getCurrentVAccount(profile, session),
            ...getCurrentSubVAccounts(profile, session),
        ],
        []
    );
    const [currentVAccount, setCurrentVAccount] = useState(
        subVAccounts[0] || null
    );
    let pageTitle = languageManager.T(
        'SETTINGS_PAGE_SETTINGS_ACCESS_MANAGEMENT'
    );

    useEffect(() => {
        const job = async () => {
            setLoadingStatus(true);
            try {
                await refreshUserPermission(currentVAccount.entityId);
            } catch (e) {
                console.error(e);
            }
            setLoadingStatus(false);
        };
        job();
    }, [currentVAccount]);

    const refreshUserPermission = async (entityId) => {
        try {
            // const pMaxPermRes = await User.getMaxPermissionForPortal(entityId, params.userId);
            // const uPermRes = await Admin.getPortalPermission(entityId, params.userId);
            // const uDrivePermRes = await Admin.getDrivePermissions(entityId, params.userId);
            const hasFFDAPermission = PM.isFederated(session, profile);
            const [pMaxPermRes, uPermRes, uDrivePermRes, uReportPermiRes] =
                await Promise.all([
                    User.getMaxPermissionForPortal(entityId, params.userId),
                    Admin.getPortalPermission(entityId, params.userId),
                    Admin.getDrivePermissions(entityId, params.userId),
                    new Promise(async (resolve) => {
                        if (hasFFDAPermission) {
                            const res = await Admin.getReportingPermissions(
                                entityId,
                                params.userId
                            );
                            return resolve(res);
                        }
                        return resolve(null);
                    }),
                ]);
            setUserPermission(uPermRes.data);
            setMaxPortalPermission(pMaxPermRes.data);
            setUserDrivePermission(uDrivePermRes.data);
            console.log('uReportPermiRes:', uReportPermiRes, hasFFDAPermission);
            if (hasFFDAPermission) {
                setUserReportingPermission(uReportPermiRes.data);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const handleUpdateUserDrivePermission = async (permissions) => {
        setLoadingStatus(true);
        const entityId = currentVAccount.entityId;
        UserAnalytic.event({
            category: 'settings_user_management',
            action: 'Update Permissions',
            label: `Update Drive Permissions`,
        });
        try {
            const uDrivePermRes = await Admin.setDrivePermission(
                entityId,
                params.userId,
                permissions
            );
            setUserDrivePermission(uDrivePermRes.data);
        } catch (e) {
            console.error(e);
        }
        setLoadingStatus(false);
    };

    const handleUpdateUserPortalPermission = async (permissions) => {
        setLoadingStatus(true);
        const entityId = currentVAccount.entityId;
        UserAnalytic.event({
            category: 'settings_user_management',
            action: 'Update Permissions',
            label: `Update Portal Permissions`,
        });
        try {
            const uDrivePermRes = await Admin.setPortalPermission(
                entityId,
                params.userId,
                permissions
            );
            setUserPermission(uDrivePermRes.data);
        } catch (e) {
            console.error(e);
        }
        setLoadingStatus(false);
    };

    const handleUpdateUserReportingPermission = async (
        entityId,
        permissionId,
        permissions
    ) => {
        setLoadingStatus(true);
        UserAnalytic.event({
            category: 'settings_user_management',
            action: 'Update Permissions',
            label: `Update Reporting Permissions`,
        });
        try {
            const uReportPermiRes = await Admin.setReportingPermissions(
                entityId,
                permissionId,
                permissions
            );
            const newPermission = uReportPermiRes.data;
            const index = uReportingPermission.findIndex(
                (p) => p.id === permissionId
            );
            uReportingPermission[index] = newPermission;
            setUserReportingPermission([...uReportingPermission]);
        } catch (e) {
            console.error(e);
        }
        setLoadingStatus(false);
    };

    const refreshUserInformation = async () => {
        UserAnalytic.event({
            category: 'settings_user_management',
            action: 'Refresh User Info',
        });
        try {
            const d = await User.getUserInformation(params.userId);
            setCurrentUser(d.data);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        if (isInit) return;
        setInitStatus(true);
        const job = async () => {
            setLoadingStatus(true);
            await refreshUserInformation();
            await refreshUserPermission(currentVAccount.entityId);
            setLoadingStatus(false);
        };
        job();
    }, [isInit]);
    const safeUser = currentUser || {};
    return (
        <>
            {isLoading ? <Overlay show /> : null}
            <MainLayout pageTitle={pageTitle}>
                <Container className="settings-module">
                    <Row className="mt-4">
                        <Col lg={12}>
                            <Button
                                onClick={() =>
                                    navigateManager(
                                        '/settings/access-management'
                                    )
                                }
                            >
                                {languageManager.T('GEN_BACK')}
                            </Button>
                        </Col>
                    </Row>
                    <Row className="mt-4">
                        <Col lg={4} md={6}>
                            <Card className="shadow-sm">
                                <Card.Body>
                                    <div className="d-flex">
                                        <div className="flex-shrink-0">
                                            <img
                                                src={AnonymousAvatarURL}
                                                className="rounded"
                                                width="64px"
                                            />
                                        </div>
                                        <div className="flex-grow-1 ms-3">
                                            <div className="h4">
                                                {safeUser.firstname}{' '}
                                                {safeUser.lastname}
                                            </div>
                                            <div>
                                                {safeUser.isBusinessOwner ===
                                                true ? (
                                                    <span className="badge bg-primary">
                                                        Business Owner
                                                    </span>
                                                ) : (
                                                    <span className="badge bg-secondary">
                                                        Regular User
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {/* <hr /> */}
                                </Card.Body>
                            </Card>
                            <ListGroup className="mt-3 shadow-sm lgi-hover">
                                <ListGroup.Item
                                    onClick={() =>
                                        navigateManager(
                                            GET_ROUTE(
                                                safeUser.userId,
                                                '/information'
                                            )
                                        )
                                    }
                                >
                                    Information
                                </ListGroup.Item>
                                <ListGroup.Item
                                    onClick={() =>
                                        navigateManager(
                                            GET_ROUTE(
                                                safeUser.userId,
                                                '/permission'
                                            )
                                        )
                                    }
                                >
                                    Permission
                                </ListGroup.Item>
                            </ListGroup>
                        </Col>
                        <Col lg={8} md={6}>
                            <Routes>
                                <Route
                                    path="/information"
                                    element={
                                        <InformationView
                                            user={safeUser}
                                            profile={profile}
                                        />
                                    }
                                />
                                <Route
                                    path="/permission"
                                    element={
                                        <PermissionView
                                            user={safeUser}
                                            profile={profile}
                                            session={session}
                                            currentVAccount={currentVAccount}
                                            setCurrentVAccount={(entityId) => {
                                                const vAccount =
                                                    subVAccounts.find(
                                                        (v) =>
                                                            v.entityId ===
                                                            entityId
                                                    );
                                                setCurrentVAccount(vAccount);
                                            }}
                                            subVAccounts={subVAccounts}
                                            permissions={userPermission}
                                            maxPortalPermission={
                                                maxPortalPermission
                                            }
                                            drivePermission={uDrivePermission}
                                            reportingPermission={
                                                uReportingPermission
                                            }
                                            handleUpdateUserPortalPermission={
                                                handleUpdateUserPortalPermission
                                            }
                                            handleUpdateUserDrivePermission={
                                                handleUpdateUserDrivePermission
                                            }
                                            handleUpdateUserReportingPermission={
                                                handleUpdateUserReportingPermission
                                            }
                                        />
                                    }
                                />
                                {safeUser.userId ? (
                                    <Route
                                        path=""
                                        element={
                                            <Navigate
                                                to={GET_ROUTE(
                                                    safeUser.userId,
                                                    '/information'
                                                )}
                                            />
                                        }
                                    />
                                ) : null}
                            </Routes>
                        </Col>
                    </Row>
                </Container>
            </MainLayout>
        </>
    );
};

const mapStateToProps = (appState) => ({
    session: appState.session,
    profile: appState.userProfile,
});

export default connect(mapStateToProps)(SettingsAccessManagementUser);
